import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    MenuItem,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from "@mui/material";
import {
    Save,
    Search,
    Edit,
    Delete,
    WaterDrop,
    Whatshot,
    AcUnit,
    Inventory2,
} from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "axios";
import db from "../firebase-config";
import { apiUrl } from "../constants";
import useCheckToken from "../hooks/useCheckToken";
import useExchangeToken from "../hooks/useExchangeToken";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

const rawProcesses = [
    { key: "coating", name: "Coating", icon: <WaterDrop /> },
    { key: "cooking", name: "Cooking", icon: <Whatshot /> },
];

const cookedProcesses = [
    { key: "cooling", name: "Cooling", icon: <AcUnit /> },
    { key: "packing", name: "Packing", icon: <Inventory2 /> },
];

const defectConfigs = {
    coating: { area: "ฝั่งดิบ", defect_code: "coating" },
    cooking: { area: "ฝั่งดิบ", defect_code: "cooking" },
    cooling: { area: "ฝั่งสุก", defect_code: "cooling" },
    packing: { area: "ฝั่งสุก", defect_code: "packing" },
};

const shiftOptions = [
    { value: "D", label: "D" },
    { value: "N", label: "N" },
];

const dateFieldStyle = {
    width: "100%",
    svg: { color: "#000000" },
    "& .MuiOutlinedInput-root": {
        borderRadius: 1.5,
        backgroundColor: "#fff",
        height: 40,
        "& fieldset": { borderColor: "#c4c2c2" },
        "&:hover fieldset": { borderColor: "#c4c2c2" },
        "&.Mui-focused fieldset": { borderColor: "#c4c2c2" },
    },
    "& .MuiOutlinedInput-input": {
        color: "#000000",
        fontWeight: "bold",
        fontSize: 16,
        height: "48px",
        padding: "0 14px",
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
    },
};

const formatThaiTime = (timestamp) => {
    if (!timestamp) return "";
    return dayjs
        .utc(timestamp)
        .tz("Asia/Bangkok")
        .format("YYYY-MM-DD HH:mm:ss");
};

export default function AddDefectPage() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const { line } = useParams();
    const exchangeToken = useExchangeToken();

    const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [tableDate, setTableDate] = useState(dayjs().format("YYYY-MM-DD"));

    const [shift, setShift] = useState("");
    const [selectedSku, setSelectedSku] = useState("");
    const [skuList, setSkuList] = useState(null);

    const [defects, setDefects] = useState({
        coating: { ng: "", remark: "" },
        cooking: { ng: "", remark: "" },
        cooling: { ng: "", remark: "" },
        packing: { ng: "", remark: "" },
    });

    const [rows, setRows] = useState([]);
    const [allRows, setAllRows] = useState([]);
    const [searchSku, setSearchSku] = useState("");

    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    const [editRow, setEditRow] = useState(null);
    const [editNg, setEditNg] = useState("");
    const [editRemark, setEditRemark] = useState("");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useCheckToken();

    const ac_token = useSelector((state) => state.auth.access_token);
    const display = useSelector((state) => state.auth.email);

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const allowOnlyInteger = (value) => {
        if (value === "") return true;
        return /^\d+(\.\d{0,1})?$/.test(value);
    };

    const toIntegerNumber = (value) => {
        const num = parseFloat(value);
        if (Number.isNaN(num) || num < 0) return 0;
        return Number(num.toFixed(1));
    };

    useEffect(() => {
        const fetchInitialTokenAndData = async () => {
            try {
                await exchangeToken();
            } catch (err) {
                console.error("Error exchanging token:", err);
            }
        };

        fetchInitialTokenAndData();
    }, [exchangeToken]);

    useEffect(() => {
        const oeeSubscribe = db
            .collection("prod_mes_ignition_oee")
            .onSnapshot((querySnapshot) => {
                const oeeDocs = [];

                querySnapshot.forEach((doc) => {
                    const data = { ...doc.data(), id: doc.id };

                    if (doc.id.includes(date) && doc.id.includes(line)) {
                        oeeDocs.push(data);
                    }
                });

                const lastOee = oeeDocs[oeeDocs.length - 1];

                if (lastOee) {
                    setSkuList(lastOee);
                } else {
                    setSkuList(null);
                }
            });

        return () => {
            oeeSubscribe();
        };
    }, [date, line]);

    const mapDefectItemToRow = (item, index = 0) => ({
        id: item.id || item.ID || `${Date.now()}-${index}`,
        defectId: item.id || item.ID,
        date: item.factory_date || "",
        sku: item.sku || "",
        shift: item.shift || "",
        side: item.area || "",
        sideEn:
            item.area === "ฝั่งดิบ"
                ? "Raw"
                : item.area === "ฝั่งสุก"
                    ? "Cooked"
                    : "",
        process: item.defect_code || "",
        ng: toIntegerNumber(item.ng_count ?? 0),
        remark: item.remark || "",
        defectTimestamp: item.defect_timestamp || "",
        updatedBy: item.created_by || item.updated_by || "",
        updateTimestamp: item.update_timestamp || "",
        updateTimestampDisplay: formatThaiTime(item.update_timestamp),
    });

    const sortOldestFirst = (data) => {
        return [...data].sort(
            (a, b) =>
                dayjs.utc(a.updateTimestamp).valueOf() -
                dayjs.utc(b.updateTimestamp).valueOf()
        );
    };

    const fetchDefectData = async (targetDate = tableDate) => {
        try {
            if (!line || !ac_token || !targetDate) return;

            setLoadingTable(true);

            const getUrl =
                `${apiUrl.GET_DEFECT_KORAT}` +
                `&line=${encodeURIComponent(line)}` +
                `&factory_date=${encodeURIComponent(targetDate)}`;

            const response = await axios.get(getUrl, {
                headers: {
                    Authorization: `Bearer ${ac_token}`,
                },
            });

            const defectList = response?.data?.data || response?.data || [];

            const tableRows = sortOldestFirst(
                defectList.map((item, index) =>
                    mapDefectItemToRow(item, index)
                )
            );
            setAllRows(tableRows);
            setRows(tableRows);
        } catch (error) {
            console.error("Get defect error:", error);
            showSnackbar("ดึงข้อมูล Defect ไม่สำเร็จ", "error");
        } finally {
            setLoadingTable(false);
        }
    };

    useEffect(() => {
        if (line && ac_token && tableDate) {
            fetchDefectData(tableDate);
        }
    }, [line, ac_token, tableDate]);

    const handleSearchSku = () => {
        const keyword = searchSku.trim().toLowerCase();

        if (!keyword) {
            setRows(allRows);
            return;
        }

        const filteredRows = allRows.filter((row) =>
            String(row.sku || "").toLowerCase().includes(keyword)
        );

        setRows(filteredRows);
    };

    const handleDefectChange = (key, field, value) => {
        setDefects((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value,
            },
        }));
    };

    const handleSave = async () => {
        try {
            if (!date) {
                showSnackbar("กรุณาเลือกวันที่", "warning");
                return;
            }

            if (!selectedSku) {
                showSnackbar("กรุณาเลือก SKU", "warning");
                return;
            }

            if (!shift) {
                showSnackbar("กรุณาเลือก Shift", "warning");
                return;
            }

            const allProcesses = [...rawProcesses, ...cookedProcesses];

            const defectRows = allProcesses.filter((item) => {
                const ngValue = toIntegerNumber(defects[item.key]?.ng || 0);
                const remarkValue = defects[item.key]?.remark || "";
                return ngValue > 0 || remarkValue.trim() !== "";
            });

            if (defectRows.length === 0) {
                showSnackbar("กรุณากรอก NG หรือ Remark อย่างน้อย 1 รายการ", "warning");
                return;
            }

            setLoadingSave(true);

            const currentDateTime = dayjs().format("YYYY-MM-DDTHH:mm:ss");

            const apiPromises = defectRows.map((item) => {
                const defectConfig = defectConfigs[item.key];
                const value = defects[item.key]?.ng || 0;
                const remark = defects[item.key]?.remark || "";

                const body = {
                    country: "THAILAND",
                    plant_id: "4117",
                    factory: "00000000000000",
                    line: line,
                    factory_date: date,
                    shift: shift,
                    sku: selectedSku,
                    area: defectConfig.area,
                    ng_count: toIntegerNumber(value),
                    defect_code: defectConfig.defect_code,
                    defect_timestamp: currentDateTime,
                    remark: remark || "",
                    created_by: display,
                    update_timestamp: currentDateTime,
                };

                return axios.post(apiUrl.POST_DEFECT_KORAT, body, {
                    headers: {
                        Authorization: `Bearer ${ac_token}`,
                    },
                });
            });

            const responses = await Promise.all(apiPromises);

            const newRows = responses.map((res, index) => {
                const apiData = res?.data?.data || res?.data || {};

                const fallbackItem = defectRows[index];
                const fallbackConfig = defectConfigs[fallbackItem.key];

                const mergedItem = {
                    id: apiData.id || apiData.ID || `${Date.now()}-${index}`,
                    factory_date: apiData.factory_date || date,
                    sku: apiData.sku || selectedSku,
                    shift: apiData.shift || shift,
                    area: apiData.area || fallbackConfig.area,
                    defect_code: apiData.defect_code || fallbackConfig.defect_code,
                    ng_count:
                        apiData.ng_count ??
                        toIntegerNumber(defects[fallbackItem.key]?.ng || 0),
                    remark:
                        apiData.remark ??
                        defects[fallbackItem.key]?.remark ??
                        "",
                    defect_timestamp:
                        apiData.defect_timestamp || currentDateTime,
                    created_by:
                        apiData.created_by || display,
                    update_timestamp:
                        apiData.update_timestamp || currentDateTime,
                };

                return mapDefectItemToRow(mergedItem, index);
            });

            setAllRows((prev) => sortOldestFirst([...prev, ...newRows]));

            setRows((prev) => {
                const keyword = searchSku.trim().toLowerCase();
            
                const mergedRows = [...prev, ...newRows];
            
                if (!keyword) {
                    return sortOldestFirst(mergedRows);
                }
            
                return sortOldestFirst(
                    mergedRows.filter((row) =>
                        String(row.sku || "").toLowerCase().includes(keyword)
                    )
                );
            });

            setDefects({
                coating: { ng: "", remark: "" },
                cooking: { ng: "", remark: "" },
                cooling: { ng: "", remark: "" },
                packing: { ng: "", remark: "" },
            });

            showSnackbar("บันทึกข้อมูล Defect สำเร็จ", "success");
        } catch (error) {
            console.error("Save defect error:", error);
            showSnackbar("บันทึกข้อมูล Defect ไม่สำเร็จ", "error");
        } finally {
            setLoadingSave(false);
        }
    };

    const handleOpenEdit = (row) => {
        setEditRow(row);
        setEditNg(String(toIntegerNumber(row.ng || 0)));
        setEditRemark(row.remark || "");
    };

    const handleCancelEdit = () => {
        setEditRow(null);
        setEditNg("");
        setEditRemark("");
    };

    const handleUpdateDefect = async () => {
        try {
            if (!editRow?.defectId) {
                showSnackbar("ไม่พบ ID สำหรับแก้ไขข้อมูล", "error");
                return;
            }

            setLoadingEdit(true);

            const currentDateTime = dayjs().format("YYYY-MM-DDTHH:mm:ss");

            const patchPayload = {
                ng_count: toIntegerNumber(editNg),
                remark: editRemark || "",
                updated_by: display,
                update_timestamp: currentDateTime,
            };

            await axios.patch(
                `${apiUrl.POST_DEFECT_KORAT}${editRow.defectId}/`,
                patchPayload,
                {
                    headers: {
                        Authorization: `Bearer ${ac_token}`,
                    },
                }
            );

            showSnackbar("แก้ไขข้อมูล Defect สำเร็จ", "success");
            handleCancelEdit();
            fetchDefectData(tableDate);
        } catch (error) {
            console.error("Update defect error:", error);
            showSnackbar("แก้ไขข้อมูล Defect ไม่สำเร็จ", "error");
        } finally {
            setLoadingEdit(false);
        }
    };

    const handleDeleteDefect = async (row) => {
        try {
            const defectId = row?.defectId;

            if (!defectId) {
                showSnackbar("ไม่พบ ID สำหรับลบข้อมูล", "error");
                return;
            }

            setLoadingDelete(true);

            await axios.delete(`${apiUrl.POST_DEFECT_KORAT}${defectId}/`, {
                headers: {
                    Authorization: `Bearer ${ac_token}`,
                },
            });

            showSnackbar("ลบข้อมูล Defect สำเร็จ");
            fetchDefectData(tableDate);
        } catch (error) {
            console.error("Delete defect error:", error);
            showSnackbar("ลบข้อมูล Defect ไม่สำเร็จ", "error");
        } finally {
            setLoadingDelete(false);
        }
    };

    const renderProcessCard = (item, color) => (
        <Grid item xs={12} md={6} key={item.key}>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    height: "100%",
                    borderColor: color.border,
                    bgcolor: "#fff",
                }}
            >
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                bgcolor: color.light,
                                color: color.main,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {item.icon}
                        </Box>

                        <Typography fontWeight="bold" sx={{ color: color.main }}>
                            {item.name}
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="NG Qty (kg.)"
                        value={defects[item.key].ng}
                        onChange={(e) => {
                            const value = e.target.value;

                            if (allowOnlyInteger(value)) {
                                handleDefectChange(item.key, "ng", value);
                            }
                        }}
                        inputProps={{
                            step: 0.1,
                            min: 0,
                        }}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Remark"
                        placeholder="ระบุสาเหตุ / รายละเอียด"
                        multiline
                        rows={3}
                        value={defects[item.key].remark}
                        onChange={(e) =>
                            handleDefectChange(item.key, "remark", e.target.value)
                        }
                    />
                </CardContent>
            </Card>
        </Grid>
    );

    return (
        <>
            <Helmet>
                <title>Defect | MES</title>
            </Helmet>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ p: 3, bgcolor: "#f6f8fc", minHeight: "100vh" }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h4" fontWeight="bold">
                            Add Defect
                        </Typography>
                        <Typography color="text.secondary">
                            บันทึกข้อมูล Defect การผลิต
                        </Typography>
                    </Box>

                    <Card sx={{ borderRadius: 3, mb: 2 }}>
                        <CardContent>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ mb: 2, color: "#1565c0" }}
                            >
                                บันทึกข้อมูล Defect
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <DatePicker
                                        inputFormat="YYYY-MM-DD"
                                        value={dayjs(date)}
                                        onChange={(newValue) => {
                                            if (newValue && newValue.isValid()) {
                                                setDate(newValue.format("YYYY-MM-DD"));
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                size="small"
                                                {...params}
                                                label="Factory Date *"
                                                sx={dateFieldStyle}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="SKU *"
                                        value={selectedSku}
                                        onChange={(e) => setSelectedSku(e.target.value)}
                                    >
                                        {(skuList?.SKU_LIST || []).map((item) => (
                                            <MenuItem key={item.sku} value={item.sku}>
                                                {item.sku}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Shift *"
                                        value={shift}
                                        onChange={(e) => setShift(e.target.value)}
                                    >
                                        {shiftOptions.map((item) => (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    border: "1px solid #bbdefb",
                                    bgcolor: "#f7fbff",
                                }}
                            >
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{ color: "#0d6efd", mb: 2 }}
                                    >
                                        💧 ฝั่งดิบ (Raw Side)
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {rawProcesses.map((item) =>
                                            renderProcessCard(item, {
                                                main: "#0d6efd",
                                                light: "#e8f1ff",
                                                border: "#bbdefb",
                                            })
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    border: "1px solid #b7e4c7",
                                    bgcolor: "#f7fffa",
                                }}
                            >
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{ color: "#159947", mb: 2 }}
                                    >
                                        ♨️ ฝั่งสุก (Cooked Side)
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {cookedProcesses.map((item) =>
                                            renderProcessCard(item, {
                                                main: "#159947",
                                                light: "#e9f8ef",
                                                border: "#b7e4c7",
                                            })
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={loadingSave}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                height: 40,
                                bgcolor: "#1565ff",
                            }}
                        >
                            {loadingSave ? "Saving..." : "Save Defect"}
                        </Button>
                    </Box>

                    <Card sx={{ mt: 3, borderRadius: 3 }}>
                        <CardContent>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="primary"
                                sx={{ mb: 2 }}
                            >
                                ข้อมูล Defect
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexWrap: "wrap",
                                    mb: 2,
                                }}
                            >
                                <DatePicker
                                    inputFormat="YYYY-MM-DD"
                                    value={dayjs(tableDate)}
                                    onChange={(newValue) => {
                                        if (newValue && newValue.isValid()) {
                                            setSearchSku("");
                                            setTableDate(newValue.format("YYYY-MM-DD"));
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            size="small"
                                            {...params}
                                            label="วันที่แสดงข้อมูล"
                                            sx={{
                                                width: 180,
                                                "& .MuiOutlinedInput-root": {
                                                    height: 38,
                                                    borderRadius: 1.5,
                                                    backgroundColor: "#fff",
                                                },
                                            }}
                                        />
                                    )}
                                />

                                <TextField
                                    size="small"
                                    label="ค้นหา SKU"
                                    placeholder="ชื่อ SKU"
                                    value={searchSku}
                                    onChange={(e) => setSearchSku(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSearchSku();
                                        }
                                    }}
                                    sx={{
                                        width: 320,
                                        "& .MuiOutlinedInput-root": {
                                            height: 38,
                                            borderRadius: 1.5,
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<Search />}
                                    onClick={handleSearchSku}
                                    sx={{
                                        height: 38,
                                        px: 3,
                                        borderRadius: 1.5,
                                        bgcolor: "#1976d2",
                                    }}
                                >
                                    ค้นหา
                                </Button>
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "#f0f4fb" }}>
                                            <TableCell>No.</TableCell>
                                            <TableCell>Factory Date</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>Shift</TableCell>
                                            <TableCell>Side</TableCell>
                                            <TableCell>Process</TableCell>
                                            <TableCell align="center">NG Qty</TableCell>
                                            <TableCell sx={{ width: 200 }}>Remark</TableCell>
                                            <TableCell>Create By</TableCell>
                                            <TableCell>Update Time</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={11} align="center">
                                                    {loadingTable ? "Loading..." : "No data"}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            rows.map((row, index) => (
                                                <TableRow key={row.id}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{row.date}</TableCell>
                                                    <TableCell>{row.sku}</TableCell>
                                                    <TableCell>{row.shift}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={`${row.side} ${row.sideEn ? `(${row.sideEn})` : ""
                                                                }`}
                                                            color={
                                                                row.side === "ฝั่งดิบ"
                                                                    ? "primary"
                                                                    : "success"
                                                            }
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={row.process}
                                                            color={
                                                                row.side === "ฝั่งดิบ"
                                                                    ? "primary"
                                                                    : "success"
                                                            }
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "red" }}>
                                                        {Number(row.ng || 0).toFixed(1)}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            width: 120,
                                                            maxWidth: 120,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        <Tooltip title={row.remark || ""}>
                                                            <span>{row.remark}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>{row.updatedBy}</TableCell>
                                                    <TableCell>
                                                        {row.updateTimestampDisplay}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleOpenEdit(row)}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>

                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={loadingDelete}
                                                            onClick={() => handleDeleteDefect(row)}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    <Dialog
                        open={!!editRow}
                        onClose={handleCancelEdit}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>แก้ไข Defect</DialogTitle>

                        <DialogContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    mt: 1,
                                }}
                            >
                                <TextField
                                    size="small"
                                    label="Factory Date"
                                    value={editRow?.date || ""}
                                    disabled
                                    fullWidth
                                />

                                <TextField
                                    size="small"
                                    label="SKU"
                                    value={editRow?.sku || ""}
                                    disabled
                                    fullWidth
                                />

                                <TextField
                                    size="small"
                                    label="Process"
                                    value={editRow?.process || ""}
                                    disabled
                                    fullWidth
                                />

                                <TextField
                                    size="small"
                                    type="number"
                                    label="NG Qty"
                                    value={editNg}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (allowOnlyInteger(value)) {
                                            setEditNg(value);
                                        }
                                    }}
                                    inputProps={{
                                        step: 0.1,
                                        min: 0,
                                    }}
                                    fullWidth
                                />

                                <TextField
                                    size="small"
                                    label="Remark"
                                    value={editRemark}
                                    onChange={(e) => setEditRemark(e.target.value)}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                            </Box>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleCancelEdit} disabled={loadingEdit}>
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleUpdateDefect}
                                disabled={loadingEdit}
                            >
                                {loadingEdit ? "Saving..." : "Save"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            variant="filled"
                            sx={{ width: "100%" }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            </LocalizationProvider>
        </>
    );
}